import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Globe, Palette, Users, UserCheck, Headphones, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLang } from './LanguageProvider';
import translations from '../translations';

const featureIcons = [Globe, Palette, Users, UserCheck, Headphones, BarChart3];

function PopupContent({ onClose }) {
  const { lang } = useLang();
  const t = translations[lang].enterpriseDemo;
  const navigate = useNavigate();

  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY;
    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      htmlOverflow: html.style.overflow,
    };
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    return () => {
      body.style.overflow = prev.overflow;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      html.style.overflow = prev.htmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleDemo = () => {
    onClose();
    navigate('/contact?demo=enterprise');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 480,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          borderRadius: 24,
          background: 'linear-gradient(145deg, rgba(15,23,42,0.98), rgba(15,23,42,0.95))',
          border: '1px solid rgba(6,182,212,0.2)',
          boxShadow: '0 0 60px rgba(6,182,212,0.15), 0 25px 50px rgba(0,0,0,0.5)',
        }}
      >
        {/* Top gradient accent */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            borderRadius: '24px 24px 0 0',
            background: 'linear-gradient(90deg, #06b6d4, #8b5cf6, #ec4899)',
          }}
        />

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            width: 32,
            height: 32,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)',
            cursor: 'pointer',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <X size={16} color="rgba(255,255,255,0.6)" />
        </button>

        <div style={{ padding: '24px 24px 28px' }}>
          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(6,182,212,0.2), rgba(139,92,246,0.2))',
                  border: '1px solid rgba(6,182,212,0.3)',
                }}
              >
                <Globe size={16} color="#22d3ee" />
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#22d3ee' }}>Enterprise</span>
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em', marginBottom: 8, lineHeight: 1.3 }}>{t.title}</h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>{t.subtitle}</p>
          </div>

          {/* Features */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
            {t.features.map((feat, i) => {
              const Icon = featureIcons[i];
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      background: 'linear-gradient(135deg, rgba(6,182,212,0.12), rgba(139,92,246,0.08))',
                      border: '1px solid rgba(6,182,212,0.2)',
                    }}
                  >
                    <Icon size={15} color="#22d3ee" />
                  </div>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>{feat}</span>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <button
            onClick={handleDemo}
            style={{
              width: '100%',
              height: 48,
              borderRadius: 14,
              fontSize: 14,
              fontWeight: 600,
              color: '#0f172a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              cursor: 'pointer',
              border: 'none',
              background: 'linear-gradient(135deg, #06b6d4, #22d3ee)',
              boxShadow: '0 0 0 1px rgba(6,182,212,0.35), 0 8px 24px rgba(6,182,212,0.25)',
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.filter = 'brightness(1)'; }}
          >
            {t.cta}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EnterpriseDemoPopup({ isOpen, onClose }) {
  if (!isOpen) return null;
  return createPortal(
    <PopupContent onClose={onClose} />,
    document.body
  );
}
